import React, { createContext, ReactNode, useContext } from "react";
import ValidatorRegistry from "./utils/ValidatorRegistry";

const KNOWN_IDENTITY_PROVIDER_TYPES = ["internal", "external"];

interface HeimdallContextProps {
    validatorRegistry: ValidatorRegistry;
}

const HeimdallContextContext = createContext<HeimdallContextProps | undefined>(undefined);

export const HeimdallContextProvider: React.FC<{
    context: HeimdallContext;
    children: ReactNode;
}> = ({ context, children }) => {
    return (
        <HeimdallContextContext.Provider
            value={{
                validatorRegistry: context.validatorRegistry,
            }}
        >
            {children}
        </HeimdallContextContext.Provider>
    );
};

export const useHeimdallContext = (): HeimdallContextProps => {
    const context = useContext(HeimdallContextContext);
    if (!context) {
        throw new Error("useHeimdallContext must be used within a HeimdallContextProvider");
    }
    return context;
};

export default class HeimdallContext {
    private _identityProviderType: string;
    readonly validatorRegistry: ValidatorRegistry;

    /**
     * A helper class to handle different app behaviours based on server side config settings
     * @param {string} identityProviderType - The type of the identity provider used. Must be one of KNOWN_IDENTITY_PROVIDER_TYPES
     */
    constructor(identityProviderType: string, validatorRegistry: ValidatorRegistry) {
        if (KNOWN_IDENTITY_PROVIDER_TYPES.indexOf(identityProviderType) === -1)
            throw new Error(`Unknown identity provider type: ${identityProviderType}`);
        this._identityProviderType = identityProviderType;
        this.validatorRegistry = validatorRegistry;
    }

    get isInternalIdenityProvider() {
        return this._identityProviderType == "internal";
    }
}
